/**
 * ASCII Primitives used in documentation:
 *
 * Timeline:
 * --------------------------
 *
 * Timeline with intervals:
 * |---------------------|------------------------|-----------------------|
 *
 * Timeline with labeled intervals:
 * |----Interval A---------|------Interval B---------|-----Interval C-------|
 *
 * Timeline with dates:
 * [date]----------[date]------------[date]----------[date]
 *
 * Timeline with dates and labeled intervals:
 * [date]----Interval A---------[date]------Interval B---------[date]-----Interval C-------[date]
 *
 * Point in time:
 * --------------*------------------------
 *
 * Labeled point in time on timeline:
 * --------------*------------------------
 *            Point X
 */

interface Interval {
  start: Date;
  end: Date;
}

/**
 * Checks if one interval is completely contained within another.
 *
 * Visual examples:
 * ```
 * Example 1: A contained in B - returns true
 *           [start A]----Interval A----[end A]
 * [start B]---------------------------------------Interval B----------------[end B]
 *
 * Example 2: A not contained in B - returns false
 * [start A]---------------------------------------Interval A----------------[end A]
 *           [start B]----Interval B----[end B]
 *
 * Example 3: A partially overlaps B - returns false
 *           [start A]----Interval A----------------[end A]
 * [start B]----Interval B----------------[end B]
 * ```
 *
 * @param intervalA - The interval to check if it's contained
 * @param intervalB - The interval that might contain intervalA
 * @returns true if intervalA is completely contained within intervalB
 */
export function isIntervalAContainedByIntervalB(intervalA: Interval, intervalB: Interval): boolean {
  return intervalA.start >= intervalB.start && intervalA.end <= intervalB.end;
}

/**
 * Checks if two intervals overlap at all.
 *
 * Visual examples:
 * ```
 * Example 1: Partial overlap - returns true
 * [start A]------------Interval A----------------------------[end A]
 *                                     [start B]------Interval B-------------------------------[end B]
 *
 * Example 2: Complete overlap - returns true
 * [start A]------------Interval A----------------------------[end A]
 * [start B]------------Interval B----------------------------[end B]
 *
 * Example 3: One contains the other - returns true
 * [start A]------------Interval A----------------------------[end A]
 *           [start B]------Interval B------[end B]
 *
 * Example 4: Adjacent intervals (no overlap) - returns false
 * [start A]------------Interval A------------[end A][start B]------------Interval B------------[end B]
 * ```
 *
 * @param intervalA - First interval to check
 * @param intervalB - Second interval to check
 * @returns true if the intervals overlap at all
 */
export function doIntervalsOverlap(intervalA: Interval, intervalB: Interval): boolean {
  return intervalA.start <= intervalB.end && intervalA.end >= intervalB.start;
}

/**
 * Checks if a specific point in time falls within an interval.
 *
 * Visual examples:
 * ```
 * Example 1: Point inside interval - returns true
 * [start]------------Interval----------------------------[end]
 *                 *
 *                 Point
 *
 * Example 2: Point before interval - returns false
 * *               [start]------------Interval----------------------------[end]
 * Point
 *
 * Example 3: Point after interval - returns false
 * [start]------------Interval----------------------------[end]               *
 *                                                                        Point
 * ```
 *
 * Boundary behavior:
 * - includeStartBoundary: if true, point can be on start boundary (>= start)
 * - includeEndBoundary: if true, point can be on end boundary (<= end)
 *
 * Common combinations:
 * - Both true: point can be on either boundary (>= start && <= end)
 * - Both false: point must be strictly inside (> start && < end)
 * - Start false, end true: point must be after start and can be on end (> start && <= end)
 * - Start true, end false: point can be on start and must be before end (>= start && < end)
 *
 * @param point - The point in time to check
 * @param interval - The interval to check against
 * @param includeStartBoundary - Whether to include the start boundary (default: true)
 * @param includeEndBoundary - Whether to include the end boundary (default: true)
 * @returns true if the point falls within the interval according to the boundary settings
 */
export function isPointInInterval(
  point: Date,
  interval: Interval,
  includeStartBoundary: boolean = true,
  includeEndBoundary: boolean = true,
): boolean {
  let isPointInInterval = false;
  if (includeStartBoundary && includeEndBoundary) {
    isPointInInterval = point >= interval.start && point <= interval.end;
  }
  if (!includeStartBoundary && !includeEndBoundary) {
    isPointInInterval = point > interval.start && point < interval.end;
  }
  if (!includeStartBoundary && includeEndBoundary) {
    isPointInInterval = point > interval.start && point <= interval.end;
  }
  if (includeStartBoundary && !includeEndBoundary) {
    isPointInInterval = point >= interval.start && point < interval.end;
  }
  return isPointInInterval;
}

/**
 * Checks if two intervals are adjacent (one ends where the other begins).
 *
 * Visual examples:
 * ```
 * Example 1: Adjacent intervals - returns true
 * [start A]------------Interval A------------[end A][start B]------------Interval B------------[end B]
 *
 * Example 2: Overlapping intervals - returns false
 * [start A]------------Interval A----------------------------[end A]
 *                 [start B]------Interval B------[end B]
 *
 * Example 3: Gap between intervals - returns false
 * [start A]------------Interval A------------[end A]    [start B]------------Interval B------------[end B]
 * ```
 *
 * @param intervalA - First interval to check
 * @param intervalB - Second interval to check
 * @returns true if the intervals are adjacent
 */
export function areIntervalsAdjacent(intervalA: Interval, intervalB: Interval): boolean {
  return intervalA.end.getTime() === intervalB.start.getTime();
}

/**
 * Calculates the total duration of an interval.
 *
 * Visual examples:
 * ```
 * Example 1: Standard interval
 * [start]--------------------------------------------[end]
 * Span = end - start
 *
 * Example 2: Zero-length interval
 * [start][end]
 * Span = 0
 *
 * Example 3: Long interval
 * [start]----------------------------------------------------------------[end]
 * Span = end - start
 * ```
 *
 * @param interval - The interval to measure
 * @returns The duration in milliseconds
 */
export function getIntervalSpan(interval: Interval): number {
  return interval.end.getTime() - interval.start.getTime();
}

/**
 * Gets the overlapping portion of two intervals.
 *
 * Visual examples:
 * ```
 * Example 1: Overlapping intervals - returns intersection
 * [start A]------------Interval A----------------------------------------[end A]
 *                                     [start B]------Interval B-------------------------------[end B]
 *                                     [start I]------Intersection---------[end I]
 *
 * Example 2: No overlap - returns null
 * [start A]------------Interval A------------[end A]    [start B]------------Interval B------------[end B]
 *
 * Example 3: One contains the other - returns contained interval
 * [start A]------------Interval A----------------------------[end A]
 *           [start B]------Interval B------[end B]
 *           [start I]------Intersection----[end I]
 * ```
 *
 * @param intervalA - First interval
 * @param intervalB - Second interval
 * @returns The overlapping interval or null if there is no overlap
 */
export function getIntervalIntersection(intervalA: Interval, intervalB: Interval): Interval | null {
  const start = new Date(Math.max(intervalA.start.getTime(), intervalB.start.getTime()));
  const end = new Date(Math.min(intervalA.end.getTime(), intervalB.end.getTime()));
  return start <= end ? { start, end } : null;
}
